import React, { MutableRefObject } from 'react';
import { styled } from '@mui/material/styles';
import * as d3 from 'd3';
import { ScoringInformationDto, EntityScoreDto } from 'models/dtoTypes';
import drawDartChart from 'utils/charts/dartChart.js';
import withDimension from 'hoc/withDimension';
import ScoreInformation from 'components/ScoreInformation';
import { getScoreColor } from 'utils/score';

const PREFIX = 'DartChart';

const classes = {
  root: `${PREFIX}-root`,
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')(() => ({
  [`& .${classes.root}`]: {
    // flexGrow: 1,
    // backgroundColor: 'lightblue'
    backgroundColor: 'white',
    padding: 0,
    borderRadius: 5,
    textAlign: 'center',
    width: '100%',
    height: 'auto',
    color: 'white',
  },
}));

interface Segment {
  key: string;
  label: string;
  value: number;
  color?: string;
}

interface OnChangeFocusedScoreAlias {
  (focusedScoreAlias: string): void;
}

interface Props {
  width?: number;
  onChangeFocusedScoreAlias?: OnChangeFocusedScoreAlias;
  focusedScoreAlias?: string;
  targetRef?: MutableRefObject<HTMLDivElement>;
  overallScore: number;
  scores: EntityScoreDto[];
}

function DonutChart(props: Props): JSX.Element {
  const radar = React.useRef(null);
  const {
    overallScore,
    scores = [],
    width,
    focusedScoreAlias,
    onChangeFocusedScoreAlias,
    targetRef,
  } = props;

  const generateSegments = (scores: EntityScoreDto[]): Segment[] =>
    scores.map(({ scoreAlias, scoreName, score }) => {
      return {
        color: getScoreColor(scoreAlias || ''),
        label: scoreName || 'unknown',
        key: scoreAlias || 'unknown',
        value: score || 0,
      };
    });

  const handleClick = (key: string): void => {
    onChangeFocusedScoreAlias && onChangeFocusedScoreAlias(key);
  };

  const getScoreInformation = (key?: string | null): ScoringInformationDto[] | undefined => {
    if (!key) return undefined;
    const score = scores.find((s) => s.scoreAlias === key);
    return score?.information || undefined;
  };

  React.useEffect(() => {
    const options = {
      width: width || 900,
      height: 500,
      max: 1000,
      overall: overallScore,
      focusedKey: focusedScoreAlias,
    };

    drawDartChart(d3.select(radar.current), generateSegments(scores), options, handleClick); // eslint-disable-line
    return () => {
      d3.select(radar.current).select('svg').remove(); // eslint-disable-line
    };
  }, [scores, width, focusedScoreAlias]);

  return (
    <Root>
      <div className={classes.root} ref={targetRef}>
        <div ref={radar}></div>
      </div>
      <ScoreInformation information={getScoreInformation(focusedScoreAlias)} />
    </Root>
  );
}

export default withDimension(DonutChart);
